import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Check, Package, Star, Zap, Crown, TrendingUp, ArrowRight, Users,
  ShieldCheck, Target, Wallet, Headphones, BadgeCheck, ChevronDown, ChevronUp, Lock
} from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_META = {
  Basic: {
    icon: Package,
    gradient: 'from-blue-500 to-blue-700',
    lightBg: 'from-blue-50 to-blue-100',
    border: 'border-blue-500',
    accent: 'text-blue-600',
    ring: 'ring-blue-200',
    badge: null,
    tagline: 'Best to start your journey',
  },
  Advanced: {
    icon: Zap,
    gradient: 'from-indigo-500 to-blue-600',
    lightBg: 'from-indigo-50 to-blue-100',
    border: 'border-indigo-600',
    accent: 'text-indigo-600',
    ring: 'ring-indigo-200',
    badge: '🔥 MOST POPULAR',
    tagline: 'Best value for growing sellers',
  },
  Professional: {
    icon: Crown,
    gradient: 'from-amber-500 to-orange-600',
    lightBg: 'from-amber-50 to-orange-100',
    border: 'border-amber-500',
    accent: 'text-amber-600',
    ring: 'ring-amber-200',
    badge: '⭐ PREMIUM',
    tagline: 'For serious scaling entrepreneurs',
  },
};

const DEFAULT_STORE_TYPES = [
  {
    name: 'Basic', fee: 0, maxProducts: 1,
    allowedCategories: ['low'],
    features: ['1 Winning Product', 'Shopify Store Setup', 'Basic Ads Campaign', 'Order Management', 'WhatsApp Support'],
  },
  {
    name: 'Advanced', fee: 499, maxProducts: 2,
    allowedCategories: ['low', 'medium'],
    features: ['2 Winning Products', 'Shopify Store Setup', 'Advanced Ads Campaign', 'Order Management', 'Priority Support', 'Performance Analytics'],
    popular: true,
  },
  {
    name: 'Professional', fee: 1499, maxProducts: 5,
    allowedCategories: ['low', 'medium', 'high'],
    features: ['5 Winning Products', 'Premium Store Setup', 'Pro Ads Campaigns', 'Order Management', 'Dedicated Manager', 'Advanced Analytics', 'Scaling Strategy'],
  },
];

const DEFAULT_EARNINGS = {
  Basic:        { monthlyLow: 15000, monthlyHigh: 30000,  perOrderProfit: 200, ordersPerDayLow: 3,  ordersPerDayHigh: 6 },
  Advanced:     { monthlyLow: 40000, monthlyHigh: 80000,  perOrderProfit: 350, ordersPerDayLow: 5,  ordersPerDayHigh: 10 },
  Professional: { monthlyLow: 100000, monthlyHigh: 250000, perOrderProfit: 500, ordersPerDayLow: 10, ordersPerDayHigh: 25 },
};

const inr = (n) => '\u20b9' + Number(n || 0).toLocaleString('en-IN');

const CAT_LABEL = { low: 'Low Margin', medium: 'Medium Margin', high: 'High Margin' };

const PackageSelection = ({ storeTypes: propTypes = [], selected, onSelect, onNext, onBack }) => {
  const [plans, setPlans]   = useState([]);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [localSelected, setLocalSelected] = useState(selected || null);

  const storeTypes = (propTypes && propTypes.length > 0) ? propTypes : DEFAULT_STORE_TYPES;

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/plans-info`); if (Array.isArray(data?.plans)) setPlans(data.plans); } catch (e) { console.error('plans-info error', e); }
    })();
  }, []);

  const getEarnings = (name) => {
    const p = plans.find((pl) => pl.name === name);
    if (p?.potentialEarnings) return p.potentialEarnings;
    return DEFAULT_EARNINGS[name] || DEFAULT_EARNINGS.Basic;
  };

  const handleContinue = () => {
    if (localSelected) { onSelect(localSelected); onNext(); }
  };

  return (
    <div className="max-w-5xl mx-auto px-2 sm:px-0">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-100 border border-blue-300 rounded-full mb-3">
          <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 text-blue-600" />
          <span className="text-blue-700 font-semibold text-xs sm:text-sm">Step 1 of 4 — Choose Your Package</span>
        </div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">
          Pick the Store Plan That Fits Your Goal
        </h2>
        <p className="text-slate-500 text-sm sm:text-base max-w-2xl mx-auto">
          Higher tiers unlock more products, bigger earnings &amp; premium branding. All include done-for-you setup.
        </p>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-6">
        {storeTypes.map((st) => {
          const meta = PLAN_META[st.name] || PLAN_META.Basic;
          const Icon = meta.icon;
          const isSel = localSelected?.name === st.name;
          const isExpanded = expandedPlan === st.name;
          const earn = getEarnings(st.name);
          const cats = (st.allowedCategories || []).map((c) => CAT_LABEL[c] || c);

          return (
            <div key={st.name}
              className={`relative rounded-2xl border-2 overflow-hidden transition-all duration-300 cursor-pointer
                ${ isSel
                  ? `${meta.border} shadow-2xl shadow-blue-500/20 scale-[1.02] bg-gradient-to-b ${meta.lightBg}`
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-xl shadow-md'
                }`
              }
              onClick={() => setLocalSelected(st)}
              data-testid={`package-card-${st.name.toLowerCase()}`}
            >
              {/* Popular badge */}
              {meta.badge && (
                <div className={`absolute top-0 inset-x-0 bg-gradient-to-r ${meta.gradient} text-white text-[10px] sm:text-xs font-extrabold text-center py-1.5`}>
                  {meta.badge}
                </div>
              )}

              {/* Selected check */}
              {isSel && (
                <div className="absolute top-3 right-3 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center shadow-lg z-10">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}

              <div className={`p-4 sm:p-5 ${meta.badge ? 'pt-8 sm:pt-9' : 'pt-4 sm:pt-5'}`}>
                {/* Plan icon + name */}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-md flex-shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-lg sm:text-xl font-extrabold ${meta.accent}`}>{st.name}</h3>
                    <p className="text-slate-500 text-[11px] sm:text-xs leading-tight">{meta.tagline}</p>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                      {st.fee > 0 ? inr(st.fee) : 'FREE'}
                    </span>
                    {st.fee > 0 && <span className="text-slate-500 text-xs">one-time setup</span>}
                  </div>
                  {st.fee === 0 && <span className="text-xs text-green-600 font-semibold">No setup fee!</span>}
                </div>

                {/* Earnings potential */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-2.5 sm:p-3 mb-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendingUp className="w-3.5 h-3.5 text-green-600" />
                    <span className="text-[10px] sm:text-xs text-green-700 font-semibold">Potential Monthly Earnings</span>
                  </div>
                  <div className="text-base sm:text-xl font-extrabold text-green-700">
                    {inr(earn.monthlyLow)} – {inr(earn.monthlyHigh)}
                  </div>
                  <div className="text-[9px] sm:text-[10px] text-slate-500 mt-0.5">
                    ~{inr(earn.perOrderProfit)}/order · {earn.ordersPerDayLow}-{earn.ordersPerDayHigh} orders/day
                  </div>
                </div>

                {/* Products allowed */}
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 ${meta.accent}`}>
                    {st.maxProducts} Product{st.maxProducts > 1 ? 's' : ''}
                  </span>
                  {cats.map((c) => (
                    <span key={c} className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">{c}</span>
                  ))}
                </div>

                {/* Features (top 4, collapsible rest) */}
                <ul className="space-y-1.5 mb-2">
                  {(st.features || []).slice(0, isExpanded ? undefined : 4).map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] sm:text-xs text-slate-700">
                      <Check className={`w-3.5 h-3.5 ${meta.accent} flex-shrink-0 mt-0.5`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {(st.features || []).length > 4 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setExpandedPlan(isExpanded ? null : st.name); }}
                    className={`flex items-center gap-1 text-[11px] font-semibold ${meta.accent} mb-2`}
                  >
                    {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    {isExpanded ? 'Show less' : `+${(st.features || []).length - 4} more features`}
                  </button>
                )}

                {/* Select CTA */}
                <button
                  type="button"
                  onClick={() => setLocalSelected(st)}
                  className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all ${
                    isSel
                      ? `bg-gradient-to-r ${meta.gradient} text-white shadow-lg`
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                >
                  {isSel ? '✓ Selected' : 'Select Plan'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Trust bar */}
      <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 mb-6 text-xs sm:text-sm text-slate-500">
        {[
          { icon: Users,      text: '500+ Active Clients' },
          { icon: ShieldCheck, text: 'Secure Razorpay Checkout' },
          { icon: Target,     text: 'Done-for-You Setup' },
          { icon: Headphones, text: '24/7 WhatsApp Support' },
        ].map(({ icon: Icon, text }) => (
          <div key={text} className="flex items-center gap-1.5">
            <Icon className="w-4 h-4 text-blue-500" />
            <span>{text}</span>
          </div>
        ))}
      </div>

      {/* Continue */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        {onBack && (
          <Button variant="ghost" onClick={onBack} className="text-slate-500 w-full sm:w-auto">
            ← Back
          </Button>
        )}
        <Button
          onClick={handleContinue}
          disabled={!localSelected}
          data-testid="package-continue-btn"
          className={`w-full sm:w-auto px-8 py-4 text-base font-bold shadow-lg ${
            localSelected
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-blue-500/30 active:scale-95'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          {localSelected ? `Continue with ${localSelected.name}` : 'Select a Package'} <ArrowRight className="w-4 h-4 ml-2 inline" />
        </Button>
      </div>
    </div>
  );
};

export default PackageSelection;
